/**
 * Folder-tree shaping + body preview for the shared template picker. Generic over
 * the app's template row so `apps/hub` can pass its richer `EmailTemplate` and get
 * the same type back out of the tree.
 */

import type { EmailTemplateFolder, EmailTemplateLike } from "./types";

export interface FolderWithChildren<T extends EmailTemplateLike = EmailTemplateLike>
  extends EmailTemplateFolder {
  children: FolderWithChildren<T>[];
  templates: T[];
}

/** The folder a template belongs to, tolerating either the hydrated relation or the raw column. */
function templateFolderId(template: EmailTemplateLike): string | null {
  return template.folder?.id ?? template.folder_id ?? null;
}

export function buildFolderTree<T extends EmailTemplateLike>(
  folders: EmailTemplateFolder[],
  templates: T[]
): FolderWithChildren<T>[] {
  const folderMap = new Map<string, FolderWithChildren<T>>();

  folders.forEach((folder) => {
    folderMap.set(folder.id, {
      ...folder,
      children: [],
      templates: templates.filter((t) => templateFolderId(t) === folder.id),
    });
  });

  const rootFolders: FolderWithChildren<T>[] = [];

  folders.forEach((folder) => {
    const folderWithChildren = folderMap.get(folder.id)!;

    if (folder.parent_id) {
      const parent = folderMap.get(folder.parent_id);
      if (parent) {
        parent.children.push(folderWithChildren);
      }
    } else {
      rootFolders.push(folderWithChildren);
    }
  });

  return rootFolders;
}

export function hasTemplatesRecursive<T extends EmailTemplateLike>(
  folder: FolderWithChildren<T>
): boolean {
  return (
    folder.templates.length > 0 || folder.children.some((child) => hasTemplatesRecursive(child))
  );
}

/** Templates with no folder — the picker's root list. */
export function rootTemplatesOf<T extends EmailTemplateLike>(templates: T[]): T[] {
  return templates.filter((t) => templateFolderId(t) === null);
}

export function getTemplateBodyPreview(
  html: string | null | undefined,
  maxLength: number = 180
): string {
  if (!html) return "";
  const plainText = html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, String.fromCharCode(34))
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();

  if (plainText.length <= maxLength) return plainText;
  return `${plainText.slice(0, maxLength).trimEnd()}...`;
}
